import {
  auth,
  provider as googleProvider,
  githubProvider,
} from "../config/firebase";
import {
  signInWithPopup,
  signInWithRedirect,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  signOut as firebaseSignOut,
  type Auth,
  type AuthProvider,
} from "firebase/auth";

async function doSignInWithPopupFallback(p: AuthProvider | null) {
  if (!auth || !p) {
    // eslint-disable-next-line no-console
    console.warn("Auth or provider not initialized; cannot sign in.");
    return;
  }

  try {
    // prefer popup for better UX
    await signInWithPopup(auth as Auth, p as AuthProvider);
  } catch (err: any) {
    // If popups are blocked, fallback to redirect
    const code = err && err.code ? String(err.code) : "";
    // common popup errors start with 'auth/popup' or 'auth/cancelled-popup-request'
    if (
      code.includes("auth/popup") ||
      code.includes("auth/cancelled-popup-request") ||
      code.includes("auth/popup-blocked")
    ) {
      try {
        await signInWithRedirect(auth as Auth, p as AuthProvider);
      } catch (redirectErr) {
        // eslint-disable-next-line no-console
        console.error("Redirect sign-in failed:", redirectErr);
      }
      return;
    } else {
      // Handle 'account-exists-with-different-credential' by trying to
      // sign the user in with the existing provider and then linking.
      const errCode = err && err.code ? String(err.code) : "";
      if (errCode === "auth/account-exists-with-different-credential") {
        const email =
          (err && (err.customData || (err as any)).email) ||
          (err && (err.email as string)) ||
          null;
        const pendingCred = (err && (err.credential as any)) || null;

        if (!email) {
          // eslint-disable-next-line no-console
          console.error(
            "Account exists with different credential but email unavailable.",
            err,
          );
          throw err;
        }
        // Store pending credential so the UI can resolve by asking the user
        // which existing provider to use. This avoids automatic linking
        // decisions and lets the user pick.
        try {
          // weakly-typed map to hold pending credentials keyed by email
          pendingCredentialStore.set(email, pendingCred);
          // try to persist a serialized form to sessionStorage (best-effort)
          const serialized = serializeCredential(pendingCred);
          if (serialized)
            await persistPendingCredentialToSession(email, serialized);
        } catch (e) {
          // ignore persistence errors but log for debugging
          // eslint-disable-next-line no-console
          console.warn(
            "Failed to store pending credential in-memory/session:",
            e,
          );
        }

        // Get sign-in methods for this email. If this fails (for example
        // due to App Check/recaptcha issues), fall back to a Google sign-in
        // attempt to let the user finish linking manually.
        let methods: string[] | null;
        try {
          methods = await fetchSignInMethodsForEmail(auth as Auth, email);
        } catch (fetchErr) {
          // eslint-disable-next-line no-console
          console.warn(
            "fetchSignInMethodsForEmail failed; falling back to Google sign-in attempt:",
            fetchErr,
          );
          if (googleProvider) {
            try {
              const signInResult = await signInWithPopup(
                auth as Auth,
                googleProvider as AuthProvider,
              );
              if (pendingCred) {
                try {
                  await linkWithCredential(
                    signInResult.user as any,
                    pendingCred as any,
                  );
                  return;
                } catch (linkErr) {
                  // eslint-disable-next-line no-console
                  console.error(
                    "Linking pending credential after Google sign-in failed:",
                    linkErr,
                  );
                  throw linkErr;
                }
              }
              return;
            } catch (googleErr) {
              // eslint-disable-next-line no-console
              console.error("Fallback Google sign-in failed:", googleErr);
              throw err;
            }
          }
          // If googleProvider not available, rethrow original error
          throw err;
        }

        if (!methods || methods.length === 0) {
          // eslint-disable-next-line no-console
          console.error("No sign-in methods found for email:", email);
          // Inform caller that account exists but provider list is empty
          const conflictErr: any = new Error("account-exists");
          conflictErr.code = "auth/account-exists-with-different-credential";
          conflictErr.email = email;
          conflictErr.providers = [];
          throw conflictErr;
        }

        // Let the UI choose from `methods` rather than automatically
        // selecting the first provider. Throw an error containing the
        // email and available providers so the caller can present a chooser.
        const conflictErr: any = new Error("account-exists");
        conflictErr.code = "auth/account-exists-with-different-credential";
        conflictErr.email = email;
        conflictErr.providers = methods;
        throw conflictErr;
      }

      // eslint-disable-next-line no-console
      console.error("Popup sign-in failed:", err);
      throw err;
    }
  }
}

// Simple in-memory store for pending credentials keyed by email.
// This is ephemeral and only suitable for short-lived flows in the browser.
const pendingCredentialStore = new Map<string, any>();

// --- Persistence helpers (sessionStorage + Web Crypto) ---
const STORAGE_KEY = "bytemonk_pending_cred";
const STORAGE_KEY_PREFIX = "bytemonk_pending_cred:";
const KEY_STORAGE_NAME = "bytemonk_pending_cred_key";

function bufToBase64(buf: ArrayBuffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

function base64ToBuf(b64: string) {
  const str = atob(b64);
  const buf = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) buf[i] = str.charCodeAt(i);
  return buf.buffer;
}

async function getCryptoKey(): Promise<CryptoKey> {
  // Read exported raw key from sessionStorage or generate a new one
  const rawB64 = sessionStorage.getItem(KEY_STORAGE_NAME);
  if (rawB64) {
    const raw = base64ToBuf(rawB64);
    return crypto.subtle.importKey("raw", raw, "AES-GCM", true, [
      "encrypt",
      "decrypt",
    ]);
  }
  const key = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
  const exported = await crypto.subtle.exportKey("raw", key);
  sessionStorage.setItem(KEY_STORAGE_NAME, bufToBase64(exported));
  return key;
}

async function encryptForSession(obj: any): Promise<string> {
  const key = await getCryptoKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder().encode(JSON.stringify(obj));
  const cipher = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc);
  // store iv + ciphertext as base64
  const ivB64 = bufToBase64(iv.buffer);
  const ctB64 = bufToBase64(cipher);
  return ivB64 + "." + ctB64;
}

async function decryptFromSession(st: string): Promise<any | null> {
  try {
    const [ivB64, ctB64] = st.split(".");
    if (!ivB64 || !ctB64) return null;
    const key = await getCryptoKey();
    const iv = base64ToBuf(ivB64);
    const ct = base64ToBuf(ctB64);
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: new Uint8Array(iv) },
      key,
      ct,
    );
    const txt = new TextDecoder().decode(plain);
    return JSON.parse(txt);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to decrypt pending credential:", e);
    return null;
  }
}

async function persistPendingCredentialToSession(email: string, data: any) {
  try {
    const payload = { email, data, ts: Date.now() };
    const enc = await encryptForSession(payload);
    sessionStorage.setItem(STORAGE_KEY_PREFIX + email, enc);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to persist pending credential:", e);
  }
}

async function loadPendingCredentialFromSession(
  email: string,
): Promise<any | null> {
  try {
    const enc = sessionStorage.getItem(STORAGE_KEY_PREFIX + email);
    if (!enc) return null;
    const payload = await decryptFromSession(enc);
    return payload ? payload.data : null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to load pending credential from session:", e);
    return null;
  }
}

function clearPendingCredentialFromSession(email: string) {
  try {
    sessionStorage.removeItem(STORAGE_KEY_PREFIX + email);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to clear pending credential from session:", e);
  }
}

// Attempt to serialize useful fields from an AuthCredential-like object
function serializeCredential(raw: any) {
  if (!raw || typeof raw !== "object") return null;
  const out: any = { providerId: raw.providerId || raw.provider || null };
  // common token fields
  if (raw.idToken) out.idToken = raw.idToken;
  if (raw.accessToken) out.accessToken = raw.accessToken;
  if (raw.oauthToken) out.accessToken = raw.oauthToken;
  if (raw.secret) out.secret = raw.secret;
  if (raw.rawIdToken) out.idToken = raw.rawIdToken;
  return out;
}

// Try to reconstruct an AuthCredential for linking from a serialized form
async function reconstructCredential(serialized: any): Promise<any | null> {
  if (!serialized || !serialized.providerId) return null;
  const pid = serialized.providerId;
  try {
    if (pid === "google.com") {
      const { GoogleAuthProvider } = await import("firebase/auth");
      return GoogleAuthProvider.credential(
        serialized.idToken || null,
        serialized.accessToken || null,
      );
    }
    if (pid === "github.com") {
      const { GithubAuthProvider } = await import("firebase/auth");
      return GithubAuthProvider.credential(serialized.accessToken || null);
    }
    // Generic OAuthProvider reconstruction (best-effort)
    const { OAuthProvider } = await import("firebase/auth");
    const p = new OAuthProvider(pid);
    // OAuthProvider has static `credential` in newer SDKs; try to call if exists
    // runtime check for credential function on OAuthProvider
    if (typeof (p as any).credential === "function") {
      // call runtime method if present
      return (p as any).credential(serialized.accessToken || null);
    }
    return null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to reconstruct credential:", e);
    return null;
  }
}

/**
 * Resolve an account-exists conflict by signing in with the chosen provider
 * and linking the pending credential previously saved in the conflict flow.
 *
 * @param email email of the conflicted account
 * @param providerId provider id string like 'google.com' or 'github.com'
 */
export async function resolveAccountWithProvider(
  email: string,
  providerId: string,
): Promise<void> {
  if (!auth) throw new Error("Auth not initialized");

  // retrieve pending credential
  let pendingCred = pendingCredentialStore.get(email) || null;

  // if not found in memory, try to load a serialized form from sessionStorage
  if (!pendingCred) {
    const ser = await loadPendingCredentialFromSession(email);
    if (ser) {
      const reconstructed = await reconstructCredential(ser);
      if (reconstructed) pendingCred = reconstructed;
    }
  }

  // build provider instance
  let providerInstance: AuthProvider;
  if (providerId === "google.com") {
    if (!googleProvider) throw new Error("google_provider_unavailable");
    providerInstance = googleProvider as AuthProvider;
  } else if (providerId === "github.com") {
    if (!githubProvider) throw new Error("github_provider_unavailable");
    providerInstance = githubProvider as AuthProvider;
  } else if (providerId === "password") {
    // cannot sign in with password programmatically here; caller should
    // handle password reset flow.
    const e: any = new Error("password_required");
    e.code = "auth/password-required";
    throw e;
  } else {
    try {
      const { OAuthProvider } = await import("firebase/auth");
      providerInstance = new OAuthProvider(providerId) as any;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Unsupported provider instantiation failed:", e);
      throw new Error("unsupported_provider", { cause: e });
    }
  }

  // Sign in with the existing provider and then link pending credential
  const signInResult = await signInWithPopup(auth as Auth, providerInstance);

  if (pendingCred) {
    try {
      await linkWithCredential(signInResult.user as any, pendingCred as any);
    } catch (linkErr) {
      // eslint-disable-next-line no-console
      console.error("Linking pending credential failed:", linkErr);
      throw linkErr;
    } finally {
      pendingCredentialStore.delete(email);
      clearPendingCredentialFromSession(email);
    }
  }
}

export function clearPendingCredential(email: string) {
  pendingCredentialStore.delete(email);
}

export async function signInWithGoogle(): Promise<void> {
  // googleProvider is a GoogleAuthProvider instance
  return doSignInWithPopupFallback(googleProvider as any);
}

export async function signInWithGitHub(): Promise<void> {
  if (githubProvider) {
    try {
      // request email scope
      githubProvider.addScope("read:user");
      githubProvider.addScope("user:email");
    } catch {
      // provider may not expose addScope in some environments; ignore
    }
  }

  return doSignInWithPopupFallback(githubProvider as any);
}

export async function signOutUser(): Promise<void> {
  if (!auth) return;
  try {
    await firebaseSignOut(auth as Auth);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Sign out failed:", e);
  }
}

export default {
  signInWithGoogle,
  signInWithGitHub,
  signOutUser,
};
